"use strict"

import React from "react"
import ReactDOM from "react-dom"

import { withCrossHairConfig } from "../../../../config/CrossHairConfigProvider"
import { withPlotSettingsConfig } from "../../../../config/PlotSettingsConfigProvider"
import { withGraphState } from "../../../../config/GraphConfigProvider"
import { withAnnotationsConfig } from "../../../../config/AnnotationConfigProvider"

import {
  withTimeSeriesClassificationConfig,
  withRunEventLabelState,
} from "../../../../../components/shared_components_registration"
import { graphsEqual, getPlotTimeExtractor, getXValue } from "../../../resources/utils"
import PointDataOverlay from "./PointDataOverlay"

const VerticalSolidLine = ({ x1, x2, y2, colour }) => (
  <line x1={x1} y1={0} x2={x2} y2={y2} stroke={colour || "var(--lanzalake-blue-primary)"} opacity={0.8} />
)

class CrossHairLayer extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      overlayX: null,
      overlayY: null,
      annotationX: null,
      annotationY: null,
      pointData: null,
      annotationData: null,
      runEventData: [],
      rootNode: document.getElementById("container"),
    }

    this.catcherRef = this.catcherRef.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseOut = this.onMouseOut.bind(this)
    this.onClick = this.onClick.bind(this)

    this.getTimeExtractor = this.getTimeExtractor.bind(this)

    // Because this component exists in SVG-land, we'll be using
    // a React portal to inject the mouseover detail popup higher
    // up the DOM.
    this.mouseoverHolder = document.createElement("div")
    this.state.rootNode.appendChild(this.mouseoverHolder)
  }

  componentWillUnmount() {
    if (this.state.rootNode.contains(this.mouseoverHolder)) {
      this.state.rootNode.removeChild(this.mouseoverHolder)
    }
  }

  // TimeExtractor could be specific to a plot and not generalized to the view so checking for that
  // exception here (e.g. a site-wide summary plot that cannot support a 'day' view as 'day' is a run level concept)
  getTimeExtractor() {
    const {
      timeExtractor, // From PlotSettingsConfigProvider
      crossHairCurrentGraph, // From CrossHairConfigProvider
    } = this.props
    return getPlotTimeExtractor(crossHairCurrentGraph)
      ? getPlotTimeExtractor(crossHairCurrentGraph)
      : timeExtractor
  }

  render() {
    const {
      x,
      y,
      y2,
      xNotTime,
      crossHairTimes,
      crossHairCurrentGraph,
      crossHairValues,
      baseTime,
      graphKey,
      onClick,
      show_annotation_lines, // From withAnnotationsConfig
      show_annotations, // From withGraphState
    } = this.props
    const panelWidth = x.range()[1],
      panelHeight = y.range()[0]

    const showAnnotationCrosshair = show_annotation_lines && show_annotations

    // Some plots have their own timeExtractor independent of the general use timeExtractor on the page
    let greyLine = true
    const timeExtractor = this.getTimeExtractor()
    if (timeExtractor.name !== "Timestamp" && crossHairCurrentGraph?.special === "site-wide") {
      greyLine = false
    }

    return (
      <React.Fragment>
        {/* Vertical line for all timeseries x-axis plots */}
        {!xNotTime &&
          greyLine &&
          (crossHairTimes || []).map(({ time, colour }, idx) => {
            const xValue = x(timeExtractor.offsetDays(baseTime, time))
            return <VerticalSolidLine key={idx} x1={xValue} x2={xValue} y2={panelHeight} colour={colour} />
          })}

        {crossHairCurrentGraph !== undefined &&
          graphsEqual(crossHairCurrentGraph, graphKey) &&
          graphKey.special !== "microbial-community" &&
          (crossHairValues || []).map((point, idx) => {
            let yPos
            if (point.y2 !== undefined) {
              yPos = y2(point.y2)
            } else if (point.y !== undefined) {
              yPos = y(point.y)
            } else if (point.value !== undefined) {
              yPos = point.axis2 ? y2(point.value) : y(point.value)
            } else {
              return null
            }

            return (
              <line
                key={idx}
                x1={0}
                y1={yPos}
                x2={panelWidth}
                y2={yPos}
                stroke={point.colour || "var(--lanzalake-blue-primary)"}
                opacity={0.8}
              />
            )
          })}

        {/* Vertical line for a non-timeseries x-axis plot */}
        {xNotTime &&
          crossHairCurrentGraph !== undefined &&
          graphsEqual(crossHairCurrentGraph, graphKey) &&
          (crossHairValues || []).map((point, idx) => {
            let xPos
            if (point.x !== undefined) {
              xPos = x(point.x)
            }

            return <VerticalSolidLine key={idx} x1={xPos} x2={xPos} y2={panelHeight} colour={point.colour} />
          })}

        {/* Vertical line that follows user's cursor for placing annotations */}
        {showAnnotationCrosshair && this.state.annotationX !== null && (
          <>
            <line
              x1={this.state.annotationX}
              y1={0}
              x2={this.state.annotationX}
              y2={panelHeight}
              strokeDasharray="7 7 7 7"
              style={{ strokeWidth: "2" }}
              stroke={"var(--lanzalake-grey"}
              opacity={0.8}
            />
          </>
        )}

        <rect
          x={0}
          y={0}
          width={panelWidth}
          height={panelHeight}
          fill="white"
          opacity={0.001}
          onMouseEnter={this.onMouseEnter}
          onMouseMove={this.onMouseMove}
          onMouseOut={this.onMouseOut}
          onClick={onClick && this.onClick}
          ref={this.catcherRef}
        />

        {this.state.pointData &&
          ReactDOM.createPortal(
            <div
              className="solid-black-border"
              style={{
                position: "absolute",
                left: `${this.state.overlayX + 10}px`,
                top: `${this.state.overlayY + 10}px`,
                zIndex: "1800",
                background: "var(--lanzalake-white)",
                padding: "5px",
              }}
            >
              <PointDataOverlay
                pointData={this.state.pointData}
                annotationData={this.state.annotationData}
                runEventData={this.state.runEventData}
                timeExtractor={timeExtractor}
              />
            </div>,
            this.mouseoverHolder,
          )}
      </React.Fragment>
    )
  }

  catcherRef(catcher) {
    this.catcher = catcher
  }

  catcherPos(ev) {
    const bbox = this.catcher.getBoundingClientRect()
    return [ev.clientX - bbox.x, ev.clientY - bbox.y]
  }

  updateForCoords(ev) {
    const {
      x,
      y,
      y2,
      findDataForPoint,
      updateCrossHairs,
      baseTime,
      graphKey,
      findAnnotationLabelForPoint,
      downsampleObservations, // From PlotSettingsConfigProvider
      runEventLabels, // From RunLabelConfigProvider
      findRunLabelForPoint, // From RunLabelConfigProvider
      show_annotation_lines, // From withAnnotationsConfig
      show_annotations, // From withGraphState
    } = this.props

    const [mx, my] = this.catcherPos(ev)

    if (show_annotation_lines && show_annotations) {
      this.setState({
        annotationX: mx,
        annotationY: my,
      })
    }

    // Some plots have their own timeExtractor independent of the general use timeExtractor on the page
    const timeExtractor = this.getTimeExtractor()

    const highlightTimes = [],
      highlightValues = []

    // Determine `pointData`
    const pointData = findDataForPoint && findDataForPoint(mx, my, downsampleObservations)
    if (pointData && pointData.length > 0) {
      this.setState({
        overlayX: ev.pageX,
        overlayY: ev.pageY,
        pointData: pointData,
      })
      for (const point of pointData) {
        if (!point.noCrossHair) {
          const t = timeExtractor.difference(getXValue(point, timeExtractor, point?.trendDayOffset), baseTime)
          if (!highlightTimes.some((x) => x.time === t)) {
            highlightTimes.push({
              time: t,
              colour: point.colour,
            })
          }
          highlightValues.push({
            value: point.value,
            y: point.y,
            y2: point.y2,
            x: point.x,
            axis2: point.axis2,
            colour: point.colour,
          })
        }
      }

      const pointerTime = timeExtractor.difference(x.invert(mx), baseTime)
      let closestTime = null
      for (const { time } of highlightTimes) {
        if (closestTime === null || Math.abs(time - pointerTime) < Math.abs(closestTime - pointerTime)) {
          closestTime = time
        }
      }

      // For times which are "reasonably" (<10mins) close to the best-matching
      // time, de-colourize them so that outliers are highlighted
      for (const time of highlightTimes) {
        if (Math.abs(time.time - closestTime) < 1.0 / 24 / 6) {
          time.colour = null
        }
      }
    } else {
      this.setState({
        overlayX: null,
        overlayY: null,
        pointData: null,
      })
      highlightTimes.push({
        time: timeExtractor.difference(x.invert(mx), baseTime),
      })
      highlightValues.push({ value: y.invert(my), axis2: false })
    }

    // Determine Annotation Label Data - Skynaught labels
    if (findAnnotationLabelForPoint) {
      const label_details = findAnnotationLabelForPoint(timeExtractor, x, y, mx)
      this.setState({
        annotationData: label_details,
      })
    }

    if (runEventLabels.length > 0) {
      const runEventLabelDetails = findRunLabelForPoint(runEventLabels, timeExtractor, x, y, mx)
      this.setState({
        runEventData: runEventLabelDetails,
      })
    }

    if (updateCrossHairs) {
      updateCrossHairs(highlightTimes, graphKey, highlightValues)
    }
  }

  onMouseEnter(ev) {
    this.updateForCoords(ev)
  }

  onMouseMove(ev) {
    this.updateForCoords(ev)
  }

  onMouseOut(ev) {
    if (this.props.updateCrossHairs) {
      this.props.updateCrossHairs(undefined, undefined, undefined)
    }

    this.setState({
      overlayX: null,
      overlayY: null,
      pointData: null,
      annotationX: null,
      annotationY: null,
    })
  }

  onClick(ev) {
    const [mx, my] = this.catcherPos(ev)
    this.props.onClick(mx, my)
  }
}

export default withPlotSettingsConfig(
  withCrossHairConfig(
    withTimeSeriesClassificationConfig(
      withRunEventLabelState(withGraphState(withAnnotationsConfig(CrossHairLayer))),
    ),
  ),
)
